import { PropsWithChildren, ReactNode } from 'react';
import { BoxProps, Flex } from 'reflexbox';
import styled, { ThemeColors } from 'styled-components';

import {
	CircleCheckmarkIcon,
	CloseIcon,
	InfoAlternateIcon,
	StarsIcon,
	WarningIcon,
} from '../icons';
import { tokens } from '../theme/tokens';

type CalloutTone = 'info' | 'warning' | 'error' | 'success' | 'brand';

const colorToTone = (color: keyof ThemeColors): CalloutTone => {
	if (color === 'danger' || color === 'red') return 'error';
	if (color === 'warning' || color === 'yellow') return 'warning';
	if (color === 'success' || color === 'green' || color === 'greenDark') return 'success';
	if (color === 'primary' || color === 'purple') return 'brand';
	return 'info';
};

const toneTokens = (tone: CalloutTone) => {
	if (tone === 'brand') return tokens.brand;
	return tokens[tone];
};

const defaultIcon = (tone: CalloutTone) => {
	if (tone === 'warning' || tone === 'error') return <WarningIcon size={20} />;
	if (tone === 'success') return <CircleCheckmarkIcon size={20} />;
	if (tone === 'brand') return <StarsIcon size={20} />;
	return <InfoAlternateIcon size={20} />;
};

const Element = styled(Flex)<{ $tone: CalloutTone; $squared?: boolean }>`
	align-items: flex-start;
	gap: ${tokens.gap.element}px;
	box-sizing: border-box;
	min-width: 240px;
	padding: ${tokens.padding.container}px;
	background-color: ${(props) => toneTokens(props.$tone).background};
	border: 1px solid ${(props) => toneTokens(props.$tone).border};
	border-radius: ${(props) => (props.$squared ? 0 : tokens.radius.md)}px;
	color: ${(props) => toneTokens(props.$tone).text};
`;

const IconSlot = styled.div`
	flex-shrink: 0;
	width: 20px;
	height: 20px;
	color: inherit;

	svg {
		display: block;
		width: 20px;
		height: 20px;
		color: inherit;

		path {
			fill: currentColor;
		}
	}
`;

const Body = styled.div`
	display: flex;
	flex: 1 1 auto;
	flex-direction: column;
	align-items: stretch;
	gap: ${tokens.gap.list}px;
	min-width: 0;
`;

const Copy = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: ${tokens.gap.inline}px;
	width: 100%;
	color: inherit;
`;

const Title = styled.div`
	width: 100%;
	font-family: ${tokens.typography.fontFamily};
	font-size: 15px;
	font-weight: 600;
	letter-spacing: 0.15px;
	line-height: ${tokens.typography.lineHeights.heading};
	color: inherit;
`;

const Description = styled.div`
	width: 100%;
	font-family: ${tokens.typography.fontFamily};
	font-size: ${tokens.typography.sizes.body}px;
	font-weight: 450;
	letter-spacing: 0.14px;
	line-height: ${tokens.typography.lineHeights.body};
	color: inherit;
`;

const DismissButton = styled.button`
	display: flex;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	width: 20px;
	height: 20px;
	padding: 0;
	border: 0;
	background: transparent;
	color: inherit;
	cursor: pointer;

	svg {
		display: block;
		width: 20px;
		height: 20px;
		color: inherit;

		path {
			fill: currentColor;
		}
	}
`;

export function Callout({
	color,
	icon,
	title,
	description,
	children,
	squared,
	action,
	onDismiss,
	...rest
}: PropsWithChildren<
	{
		color: keyof ThemeColors;
		icon?: ReactNode | null;
		title?: ReactNode;
		description?: ReactNode;
		small?: boolean;
		squared?: boolean;
		action?: ReactNode;
		onDismiss?: () => void;
	} & BoxProps
>) {
	const tone = colorToTone(color);
	const resolvedIcon = icon === null ? null : (icon ?? defaultIcon(tone));
	const body = typeof children === 'string' ? children : undefined;
	const extra = typeof children === 'string' ? undefined : children;

	return (
		<Element $tone={tone} $squared={squared} {...rest}>
			{resolvedIcon && <IconSlot>{resolvedIcon}</IconSlot>}
			<Body>
				{(title || description || body) && (
					<Copy>
						{title && <Title>{title}</Title>}
						{(description || body) && <Description>{description ?? body}</Description>}
					</Copy>
				)}
				{action}
				{extra}
			</Body>
			{onDismiss && (
				<DismissButton type="button" aria-label="Dismiss" onClick={onDismiss}>
					<CloseIcon size={20} />
				</DismissButton>
			)}
		</Element>
	);
}
